import { forwardRef, useEffect, type HTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { dialogOverlayVariants, dialogContentVariants, type DialogContentProps } from './variants';

export interface DialogProps extends HTMLAttributes<HTMLDivElement>, DialogContentProps {
  /** Whether the dialog is visible. */
  open: boolean;
  /** Called when the user clicks the backdrop or presses Escape. */
  onClose: () => void;
  /**
   * Close on Escape (default true). Disable when content can stack another
   * document-level overlay (e.g. a result BottomSheet) that owns the Escape
   * key — both listeners fire on the same press otherwise.
   */
  closeOnEscape?: boolean;
}

/**
 * Generic modal dialog rendered via a portal.
 *
 * Handles backdrop click-to-close, Escape key, and body scroll lock.
 *
 * @example
 * ```tsx
 * <Dialog open={isOpen} onClose={() => setOpen(false)}>
 *   <h2>Title</h2>
 *   <p>Content here</p>
 * </Dialog>
 * ```
 */
const Dialog = forwardRef<HTMLDivElement, DialogProps>(
  ({ open, onClose, closeOnEscape = true, size, className, children, ...props }, ref) => {
    useEffect(() => {
      if (!open) return;

      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      function handleKeyDown(e: KeyboardEvent) {
        if (e.key === 'Escape' && closeOnEscape) onClose();
      }
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.body.style.overflow = prev;
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [open, onClose, closeOnEscape]);

    if (!open) return null;

    return createPortal(
      // data-overlay-root lets useClickOutside treat this layer as stacked
      // above any open popover (see the hook's doc comment).
      <div data-overlay-root className={dialogOverlayVariants()} onClick={onClose}>
        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          className={cn(dialogContentVariants({ size }), className)}
          onClick={(e) => e.stopPropagation()}
          {...props}
        >
          {children}
        </div>
      </div>,
      document.body,
    );
  },
);
Dialog.displayName = 'Dialog';

export { Dialog };
